---
title: Adding timers in my QMK keyboard
coverImage: /blog/assets/images/qmk-stopwatch.webp
date: 2023-11-10T04:53:49.000Z
updated: 2023-12-06T17:53:00.000Z
excerpt: How I programmed the macros on my keyboard with QMK
hidden: false
tags:
  - Workspace
  - C
---

# Timers

A timer can be used for many things. The example that gave me the idea was a stretch timer, to remind me to get up and move around every once in a while. The basic idea is that I press a button and the keyboard starts counting seconds. After however long it does something. My initial implementation sets some leds to orange and incrementally changes them to green until the timer is over and it flashes the whole underglow red as an alert, but there's no speaker on board.
One could argue that since I already have a timer on my phone and my computer, it's not particularly useful. I try not to use my phone while I'm at my computer, and any app is not going to be as convenient as clicking a button. Also it's pretty cool to make the underglow leds do something useful. I would like to add a speaker to my keyboard if there's an extra pwm pin, but I also plan on building or designing a split keyboard with one.

## My Timers

The first timer I implemented is a simple timer than counts each second until 5 minutes and shows progress with the 4 leds behind the logo on my keyboard. I added 3 more than count to 10 minutes, 30m, and an hour. All of them are started from a tapdance, more taps for a longer timer. When the timer completes, it switches the rgb mode to 'alternate' which alternates flashing the front and back row of leds in red. If I had (or add) an on board speaker I'll add an audio que.

I used a similar x-macro setup in timer.h as I did in macro.h.

```c:timer.h
#pragma once
#include "quantum.h"
#include "macro.h"

/* Define the timers
(timer name, function to call on alert, color to make the alert flash, and the duration of th e timer) */
#define TIMER_TABLE \
	TIMER(TMR_FIVE,	logo,	Blue,	300)\
	TIMER(TMR_TEN,		logo,	Green,	600)\
	TIMER(TMR_THIRTY,	logo,	Orange,	1800)\
	TIMER(TMR_SIXTY,	logo,	Red,	3600)
#define NUM_TIMERS 4

typedef enum {
	#define TIMER(a, b, c, d) a,
		TIMER_TABLE
	#undef TIMER
} timer_names;

/* Generate function prototypes */
#define TIMER(a, b, c, d) void b(int);
	TIMER_TABLE
#undef TIMER

//this is what we call to start a timer
void send_timer(int timer);

//this doesn't work yet
typedef enum  {
	Purple,
	Blue,
	Green,
	Yellow,
	Orange,
	Red,
}AlertColor;

typedef enum {
	Stopped,
	Init,
	Running,
	Alerting
}TimerStatus;
//the alert functions take an int because it needs to know which alert to check
// since the timers can share an alert function
typedef void(*alert_func_p)(int);

//the data structure that hold all of the state for every timer, paralell arrays
struct timers_struct {
	//status: 0 = stopped, 1 = init, 2 = running, 3 = alerting
	TimerStatus status[NUM_TIMERS];
	//duration in seconds
	const uint16_t duration_s[NUM_TIMERS];
	//the qmk timer_read() function produces milliseconds but uint16_t max is 65535
	uint16_t elapsed_ms[NUM_TIMERS];
	//so we need to count by seconds ourselves to have timers longer than a minute
	uint16_t elapsed_s[NUM_TIMERS];
	//right now all the timers have the same alert function, but what if I want to change things up?
	alert_func_p alert[NUM_TIMERS];
	// what color to display for each timer alert: need to make custom rbg loops for this
	AlertColor alertColor[NUM_TIMERS];
};

```

With all of that, we can generate the state struct, and get some timers timing!

```c:timer.c
#include "timer.h"
// #include "deferred_exec.h"
// deferred exec is a way to make qmk handle the timing.
//it can only have so many, and you only get a callback when the thing is done
// would be great for other features but I want to update the leds during the timer
// so I need to hook into the update loop (matrix_scan_user)
#define RGB_MODE_DEFAULT RGBLIGHT_MODE_RAINBOW_SWIRL
#define RGB_MODE_ALERT RGBLIGHT_MODE_ALTERNATING
#define RGB_MODE_TESTING RGBLIGHT_MODE_STATIC_LIGHT

//check timers.h for struct explanation
struct timers_struct timers = {
	{ //status
	#define TIMER(a,b,c,d) false,
		TIMER_TABLE
	#undef TIMER
	},{ //duration_s
	#define TIMER(a,b,c,d) d,
		TIMER_TABLE
	#undef TIMER
	},{ //elapsed_ms
	#define TIMER(a,b,c,d) 0,
		TIMER_TABLE
	#undef TIMER
	},{ //elapsed_s
	#define TIMER(a,b,c,d) 0,
		TIMER_TABLE
	#undef TIMER
	},{ //alert
	#define TIMER(a,b,c,d) &b,
		TIMER_TABLE
	#undef TIMER
	},{ //alertColor
	#define TIMER(a,b,c,d) c,
		TIMER_TABLE
	#undef TIMER
	}
};

void reset_timer(int timer) {
	timers.elapsed_ms[timer] = 0;
	timers.elapsed_s[timer] = 0;
}

void logo(int timer){ // Set one of the 4 logo leds for each quarter of the duration
	if(timers.status[timer] == Alerting){
		// Setting alert color this way does not work
		// I think I'll have to make a custom rgbmode
		switch (timers.alertColor[timer]) {
			case Purple: rgblight_sethsv_noeeprom(HSV_PURPLE);
			case Blue: rgblight_sethsv_noeeprom(HSV_BLUE);
			case Green: rgblight_sethsv_noeeprom(HSV_GREEN);
			case Yellow: rgblight_sethsv_noeeprom(HSV_YELLOW);
			case Orange: rgblight_sethsv_noeeprom(HSV_ORANGE);
			case Red: rgblight_sethsv_noeeprom(HSV_RED);
		}
		rgblight_mode_noeeprom(RGB_MODE_ALERT); // this flashes red even after setting the color
		if( timers.elapsed_s[timer] >= (timers.duration_s[timer] + 5)) {
			reset_timer(timer);
			timers.status[timer] = Stopped;
			rgblight_reload_from_eeprom();
		}
	}
	else if(timers.status[timer] == Running){
		const uint16_t duration_part = timers.duration_s[timer] / 4;
		// Set one of the 4 logo leds for each quarter of the duration
		switch (timers.elapsed_s[timer] / duration_part) {
			case 0: rgblight_sethsv_at(HSV_GREEN, 19); break;
			case 1: rgblight_sethsv_at(HSV_GREEN, 18); break;
			case 2: rgblight_sethsv_at(HSV_GREEN, 17); break;
			case 3: rgblight_sethsv_at(HSV_GREEN, 16); break;
			case 4: timers.status[timer] = Alerting;
		}
	}
	else if(timers.status[timer] == Init){
		//set the rgb mode to static so it doesn't change the leds outside the timer
		//can only some leds be static for timer and the rest be animated?
		rgblight_mode_noeeprom(RGBLIGHT_MODE_STATIC_LIGHT);
		//turn off all the leds
		for( int i = 0; i <= 19; i++){
			rgblight_sethsv_at(HSV_OFF, i);
		}
		timers.status[timer] = Running;
	}
}

void matrix_scan_user(void) {
	//for each running timer, increment it each second
	// not sure on the call rate of this function
	//but it's probably not going to be off by more than a ms or 2 each second
	for(int i=0; i < NUM_TIMERS; i++){
		if(timers.status[i] > 0){
			if(timer_elapsed(timers.elapsed_ms[i])>1000) {
				timers.elapsed_s[i]++;
				timers.elapsed_ms[i] = timer_read();
				timers.alert[i](i);
			}
		}
	}
}

void send_timer(int timer) {
	reset_timer(timer);
	timers.status[timer] = Init;
	timers.elapsed_ms[timer] = timer_read();
}
```
